import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import { environment } from 'src/environments/environment';
import { CustomerdatabaseService } from './customerdatabase.service';
import { promises } from 'dns';
import { ToastController } from '@ionic/angular';
import { cfRegionLinks } from '../helper/cfRegionLinks';

interface MicrosoftTokenResponse {
  access_token: string,
  refresh_token: string,
}

@Injectable({
  providedIn: 'root'
})
export class MicrosoftService {
  auth = firebase.auth();
  firestore = firebase.firestore();
  storage = firebase.storage();
  userData: any;

  private _accessToken: string = '';

  setAccessToken(token: string) {
    this._accessToken = token;
  }

  getAccessToken() {
    return this._accessToken;
  }

  constructor(private http: HttpClient,
    private customerDB: CustomerdatabaseService,
    private toastController: ToastController,
  ) {
  }

  async microsoftLogin(): Promise<any> {
    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      prompt: 'select_account' // Prompts the user to select a Microsoft account
    });
    provider.addScope('User.Read'); // Basic profile information
    provider.addScope('Calendars.ReadWrite'); // Calendar access
    provider.addScope('Calendars.Read'); // Calendars.Read
    provider.addScope('Calendars.Read.Shared'); // Calendars.Read
    provider.addScope('Calendars.ReadWrite.Shared'); // Calendars.Read
    provider.addScope('offline_access'); // Calendars.Read
    provider.addScope('Sites.Read.All');
    provider.addScope('Sites.ReadWrite.All');
    // access to email read
    provider.addScope('Mail.Read');
    // access to email send
    provider.addScope('Mail.Send');

    try {
      const result: any = await this.auth.signInWithPopup(provider);
      console.log("result", result);
      const microsoftAccessToken = result;
      return microsoftAccessToken;
    } catch (error) {
      console.error("Microsoft login failed", error);
      throw error;
    }
  }

  async checkAuth(): Promise<any> {
    return new Promise(resolve => {
      this.auth.onAuthStateChanged(user => {
        this.userData = user;
        resolve(user);
      });
    });
  }

  logout(): Promise<void> {
    return this.auth.signOut();
  }



  updateMicrosoftTokens(code: string, state: string) {
    const randomness = sessionStorage.getItem('sv');
    let tokenForTool = state.split('-----')[1];
    let hashValue = state.split('-----')[0];
    this.checkCorrectnessHash(hashValue, tokenForTool, randomness).then((isEqual: boolean) => {
      if (!isEqual) {
        this.showErrorToast();
        return;
      }

      const body = ({
        service: 'microsoft',
        authorization_code: code,
        redirect_uri: window.location.origin + '/login'
      })
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const url = cfRegionLinks.getOAuthTokens;
      this.http.post(url, body, { headers }).subscribe((res: MicrosoftTokenResponse) => {
        this.updateMicrosoftProfile(res, state);
      });
    });
  }

  private showErrorToast() {
    this.toastController.create({
      message: "Error while trying to login with Microsoft. Please try again.",
      duration: 5000,
    }).then((toast) => {
      toast.present();
    });
  }

  /**
   * function to get the microsoft profile. Then we will save the value inside the session storage for later use. 
   * @param res the response and access token from the microsoft login.
   * @param state state for hash checking and the type of login (calendar or smtp).
   */
  private updateMicrosoftProfile(res: MicrosoftTokenResponse, state: string) {
    this.http.get('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Host: 'graph.microsoft.com',
        Authorization: `Bearer ${res.access_token}`,
      }
    }).subscribe((microsoftProfile: any) => {
      this.addTokensToSessionStorage(res.access_token, res.refresh_token, microsoftProfile, state);
    })
  }

  private addTokensToSessionStorage(accessToken: string, refreshToken: string, microsoftProfile: any, state: string) {
    sessionStorage.setItem('microsoftProfile', JSON.stringify(microsoftProfile));
    sessionStorage.setItem('microsoftAccessToken', accessToken);
    sessionStorage.setItem('microsoftRefreshToken', refreshToken);
    sessionStorage.setItem('state', state);
    this.createState(JSON.stringify(microsoftProfile), 'pv').then((hash: string) => {
      sessionStorage.setItem('profile', hash);
    });
  }

  private removeTokensFromSessionStorage() {
    sessionStorage.removeItem('microsoftAccessToken');
    sessionStorage.removeItem('microsoftRefreshToken');
    sessionStorage.removeItem('microsoftProfile');
    sessionStorage.removeItem('state');
    sessionStorage.removeItem('sv');
    sessionStorage.removeItem('pv');
    sessionStorage.removeItem('profile');
  }

  /**
   * Here we check if we have the tokens inside the session storage. If yes, that means the user has logged in with microsoft and we will save the infos in the DB. 
   * We also need to check if the hash inside the state is the same as the hash we created before to prevent attacks from bad actors. (see microsoft documentation for this).
   */
  checkForTokensMicrosoft() {
    const accessToken = sessionStorage.getItem('microsoftAccessToken');
    const refreshToken = sessionStorage.getItem('microsoftRefreshToken');
    const microsoftProfile = sessionStorage.getItem('microsoftProfile');
    const state = sessionStorage.getItem('state');
    const profile = sessionStorage.getItem('profile');
    if (accessToken && refreshToken && microsoftProfile && state) {
      const [hashValue, tokenForTool] = state.split('-----');
      const randomnessForState = sessionStorage.getItem('sv');
      const randomnessForProfile = sessionStorage.getItem('pv');
      const correctnessStateHash = this.checkCorrectnessHash(hashValue, tokenForTool, randomnessForState);
      const correctnessProfileHash = this.checkCorrectnessHash(profile, microsoftProfile, randomnessForProfile);
      Promise.all([correctnessStateHash, correctnessProfileHash]).then(([isEqual, isEqual2]) => {
        if (isEqual && isEqual2) {
          switch (tokenForTool) {
            case 'calendar':
              this.customerDB.saveMicrosoftTeamMemberCredentials(accessToken, refreshToken, microsoftProfile);
              break;
            case 'smtp':
              this.customerDB.saveMicrosoftCredentials(accessToken, refreshToken, microsoftProfile);
              this.customerDB.saveMicrosoftSMTPCredentials(JSON.parse(microsoftProfile).mail);
              break;
            default:
              break;
          }
        }
        else {
          this.showErrorToast();
        }
        this.removeTokensFromSessionStorage();
      })
    }
  }

  /**
   * For microsoft login, there are two types, for SMTP and calendar. Both uses different scopes. 
   * Then we just go to the URL from microsoft with the login page as the redirect uri (because the DB should be restarted again after redirect).
   * @param type 'calendar' | 'smtp'
   */
  login(type: 'calendar' | 'smtp') {
    const scope = type === 'calendar' ? environment.calendarGraphScopes.join('+') : environment.smtpGraphScopes.join('+');
    const url = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?'

    const clientId = environment.azureConfig.auth.clientId;
    const prompt = 'select_account';
    const redirectUri = window.location.origin + '/login';
    const responseType = 'code';
    this.createState(type, 'sv').then((hash) => {
      const state = hash + '-----' + type;
      window.location.href = `${url}client_id=${clientId}&prompt=${prompt}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&state=${state}`;
    })
  }

  /**
   * create hash from the cookie. This is needed for the state inside microsoft redirect url. It states that we need some state to check
   * so that there is no attack from bad actors. (should be okay using a cookie as we don't need some really secure secret here, but feel free to change it).
   * This function is taken from the mdn documentation. 
   * @param message the cookie of MONAPlatformToken
   * @returns a hash of the cookie
   */
  private async digestMessage(message) {
    const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgUint8); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(""); // convert bytes to hex string
    return hashHex;
  }

  /**
   * It is a bit hard as we dont have a defined user session and random value 
   * For the nonce, we use some math random that we save inside the session storage. Then we do some calculation for the nonce so that the attacker can't get the real nonce easily.
   * I don't know whether this is secure enough, but as long as the source code is not open to public, it should be okay. 
   * For the future, maybe we use an additional Pepper value from the server and also check it against the server (cloud). 
   */
  private createState(type, randomValueName: string) {
    //1. get a random value
    const randomValue = self.crypto.getRandomValues(new Uint32Array(32));
    console.log(randomValueName, randomValue);
    const randomValueString = randomValue.join('');
    sessionStorage.setItem(randomValueName, randomValueString);

    //2. hash the token with the random value
    const tokenForHash = document.cookie.split(';').find((item) => item.includes('MONAPlatformToken'));
    const textToBeHashed = tokenForHash + randomValueString;
    let newText = this.digestMessage(textToBeHashed)

    //3. hash the client id with the type
    const clientIdHashed = this.digestMessage(type + environment.azureConfig.auth.clientId)

    //4. hash the timestamp with the random value that is change to base24 multiplied with the date (act as a new random value)
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const roundedTimestamp = Math.floor(currentTimestamp / 600) * 600;
    const dateHashed = this.digestMessage(roundedTimestamp.toString() + (parseInt(randomValueString) * roundedTimestamp).toString(24).substring(2))

    //5. hash all of them together once again.
    return new Promise(resolve => {
      Promise.all([newText, clientIdHashed, dateHashed]).then((values) => {
        this.digestMessage(values[0] + values[1] + values[2]).then(hash => resolve(hash));
      })
    })
  }

  private checkCorrectnessHash(hashValue: string, saltText: string, randomValue: string) {
    const tokenForHash = document.cookie.split(';').find((item) => item.includes('MONAPlatformToken'));
    const textToBeHashed = tokenForHash + randomValue;
    const hashedTokenWithRandom = this.digestMessage(textToBeHashed)

    const clientIdHashed = this.digestMessage(saltText + environment.azureConfig.auth.clientId)

    const currentTimestamp = Math.floor(Date.now() / 1000);
    const roundedTimestamp = Math.floor(currentTimestamp / 600) * 600;
    const dateHashed = this.digestMessage(roundedTimestamp.toString() + (parseInt(randomValue) * roundedTimestamp).toString(24).substring(2))

    return new Promise((resolve) => {
      Promise.all([hashedTokenWithRandom, clientIdHashed, dateHashed]).then((values) => {
        this.digestMessage(values[0] + values[1] + values[2]).then(hash => resolve(hash === hashValue));
      })
    })
  }
}